import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { Flex } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const CruiseMonthSelector = ({ onSelectFilters }) => {
  const currentYear = dayjs().year();
  const [mode, setMode] = useState("month");
  const [yearRange, setYearRange] = useState([currentYear, currentYear + 1, currentYear + 2]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);

  // Keep previous filters to avoid unnecessary onSelectFilters calls
  const prevFiltersRef = useRef([]);

  // Notify parent whenever selections change
  useEffect(() => {
    const filters = [];

    if (selectedDates.length === 2) {
      filters.push({
        key: "date",
        label: `${selectedDates[0].format("MMMM D")} - ${selectedDates[1].format("MMMM D YYYY")}`,
      });
    } else if (selectedDates.length === 1) {
      filters.push({
        key: "date",
        label: selectedDates[0].format("MMMM D, YYYY"),
      });
    }

    selectedMonths.forEach((month) => {
      filters.push({
        key: `month-${month.label}`,
        label: month.label,
      });
    });

    // Only call onSelectFilters if filters changed (shallow compare)
    const filtersStr = JSON.stringify(filters);
    const prevFiltersStr = JSON.stringify(prevFiltersRef.current);
    if (filtersStr !== prevFiltersStr) {
      prevFiltersRef.current = filters;
      if (onSelectFilters) {
        onSelectFilters(filters);
      }
    }
  }, [selectedMonths, selectedDates, onSelectFilters]);

  const handlePrevYear = () => {
    setYearRange(yearRange.map((y) => y - 1));
  };

  const handleNextYear = () => {
    setYearRange(yearRange.map((y) => y + 1));
  };

  const toggleMonth = (monthIndex, year) => {
    const label = `${months[monthIndex]} 1 - ${months[monthIndex]} ${dayjs(
      `${year}-${monthIndex + 1}-01`
    ).daysInMonth()} ${year}`;

    const exists = selectedMonths.find((m) => m.label === label);
    if (exists) {
      setSelectedMonths(selectedMonths.filter((m) => m.label !== label));
    } else {
      setSelectedMonths([...selectedMonths, { year, month: monthIndex, label }]);
      setSelectedDates([]); // clear dates if months selected
    }
  };

  const handleClickDate = (clickedDate) => {
    if (selectedDates.length === 0) {
      setSelectedDates([clickedDate]);
      setSelectedMonths([]); // clear months if selecting dates
    } else if (selectedDates.length === 1) {
      const [first] = selectedDates;
      if (clickedDate.isBefore(first)) {
        setSelectedDates([clickedDate, first]);
      } else {
        setSelectedDates([first, clickedDate]);
      }
    } else {
      setSelectedDates([clickedDate]);
    }
  };

  const isDateInRange = (date) => {
    if (selectedDates.length === 2) {
      const [start, end] = selectedDates;
      return date.isAfter(start) && date.isBefore(end);
    }
    return false;
  };

  const isDateStart = (date) => {
    return selectedDates.length > 0 && date.isSame(selectedDates[0], "day");
  };

  const isDateEnd = (date) => {
    return selectedDates.length === 2 && date.isSame(selectedDates[1], "day");
  };

  return (
    <div className="flex flex-col items-center gap-6 bg-white rounded-2xl w-full">
      {/* Toggle */}
      <div className="flex items-center py-4 justify-center">
        <button
          className={`flex-1 w-full font-medium border-2 rounded-l-[100px] px-10 py-3 text-xl ${
            mode === "month"
              ? "text-white bg-danger border-danger "
              : "border-black"
          }`}
          onClick={() => setMode("month")}
        >
          Months
        </button>
        <button
          className={`flex-1 w-full font-medium border-2 rounded-r-[100px] px-10 py-3 text-xl ${
            mode === "date"
              ? "text-white bg-danger border-danger"
              : "border-black"
          }`}
          onClick={() => setMode("date")}
        >
          Dates
        </button>
      </div>

      {/* Month Mode */}
      {mode === "month" && (
        <Flex className="w-full mt-6" gap={20} justify="space-between">
          <button onClick={handlePrevYear} className="text-gray-500 hover:text-blue-600 text-lg">
            <LeftOutlined />
          </button>
          {yearRange.map((year) => (
            <div key={year} className="mb-6">
              <div className="text-xl font-semibold text-gray-700 text-center mb-2">{year}</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 w-full justify-items-center">
                {months.map((month, i) => {
                  const label = `${months[i]} 1 - ${months[i]} ${dayjs(
                    `${year}-${i + 1}-01`
                  ).daysInMonth()} ${year}`;
                  const isSelected = selectedMonths.some((sel) => sel.label === label);

                  return (
                    <button
                      key={`${month}-${year}`}
                      onClick={() => toggleMonth(i, year)}
                      className={`px-4 py-2 rounded-lg border w-full text-center transition-all ${
                        isSelected
                          ? "bg-blue-600 text-white border-blue-600"
                          : "hover:bg-blue-50 border-gray-300 text-gray-700"
                      }`}
                    >
                      {month.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <button onClick={handleNextYear} className="text-gray-500 hover:text-blue-600 text-lg">
            <RightOutlined />
          </button>
        </Flex>
      )}

      {/* Date Mode */}
      {mode === "date" && (
        <Flex className="w-full mt-6" gap={40} justify="space-between">
          {[10, 11, 12].map((month) => {
            const year = 2025;
            const daysInMonth = dayjs(`${year}-${month}-01`).daysInMonth();
            const firstDay = dayjs(`${year}-${month}-01`).day();
            const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

            return (
              <div key={month}>
                <div className="text-center font-semibold text-gray-700 mb-2">
                  {months[month - 1]} {year}
                </div>
                <div className="grid grid-cols-7 gap-1 text-sm text-gray-600 text-center mb-1">
                  {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                    <div key={d}>{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: firstDay }).map((_, i) => (
                    <div key={`empty-${i}`} />
                  ))}
                  {days.map((day) => {
                    const date = dayjs(`${year}-${month}-${day}`);
                    return (
                      <button
                        key={day}
                        onClick={() => handleClickDate(date)}
                        className={`w-8 h-8 rounded-full text-sm ${
                          isDateStart(date) || isDateEnd(date)
                            ? "bg-blue-600 text-white"
                            : isDateInRange(date)
                            ? "bg-blue-100 text-blue-800"
                            : "hover:bg-blue-100 text-gray-800"
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </Flex>
      )}
    </div>
  );
};

export default CruiseMonthSelector;
